import { useEffect, useState } from "react";
import { formatIntToCurrency } from "@repo/utils";
import { ProductModifierSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { ProductModifier } from "~/providers/product-provider";
import { useGeneral } from "~/hooks/use-general";
import { useProduct } from "~/hooks/use-product";
import { cn } from "~/lib/utils";
import { useProductModifierManager } from "~/managers/use-product-modifier-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import ProductPriceForm from "../miscellaneous/price-form";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

export function ModifierForm({
  modifier,
  onClose,
}: {
  modifier?: ProductModifier;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");

  const { setUnsavedChanges } = useUnsavedChanges();

  const { updateProductModifier, createProductModifier } =
    useProductModifierManager();

  const { modifierGroups } = useProduct();

  const [priceFormOpen, setPriceFormOpen] = useState(false);

  const [selectedPrice, setSelectedPrice] = useState<
    | {
        priceId: string;
        value: number;
      }
    | undefined
  >(undefined);

  const { prices, vatRates, turnoverGroups } = useGeneral();

  const turnoverGroupOptions = [
    ...turnoverGroups.map((turnoverGroup) => ({
      value: turnoverGroup.id,
      label: turnoverGroup.name,
    })),
  ];

  const form = useForm({
    schema: ProductModifierSchema,
    reValidateMode: "onChange",
    defaultValues: {
      name: modifier?.name ?? "",
      position: modifier?.position ?? 1,
      vatRateId: modifier?.vatRateId ?? "",
      modifierGroupIds: modifier?.modifierGroupIds ?? [],
      turnoverGroupId: modifier?.turnoverGroupId ?? undefined,
      prices:
        modifier?.prices.map((price) => ({
          priceId: price.priceId,
          value: price.value,
        })) ?? [],
    },
  });

  const { reset } = form;

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handlePriceFormOpen = ({
    priceId,
    value,
  }: {
    priceId?: string;
    value?: number;
  }) => {
    setPriceFormOpen(true);
    if (priceId && value) {
      setSelectedPrice({ priceId, value });
    }
  };

  const handlePriceFormClose = () => {
    setPriceFormOpen(false);
    setSelectedPrice(undefined);
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    if (modifier) {
      const result = await updateProductModifier(modifier.id, data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const result = await createProductModifier(data);
      if (result === true) {
        form.reset();
        onClose(true);
      }
    }
  });

  const modifierGroupOptions = [
    ...modifierGroups.map((group) => ({
      value: group.id,
      label: group.name,
    })),
  ];

  const vatRateOptions = [
    ...vatRates.map((vatRate) => ({
      value: vatRate.id,
      label: vatRate.name,
    })),
  ];

  useEffect(() => {
    reset({
      name: modifier?.name ?? "",
      position: modifier?.position ?? 1,
      vatRateId: modifier?.vatRateId ?? "",
      modifierGroupIds: modifier?.modifierGroupIds ?? [],
      prices:
        modifier?.prices.map((price) => ({
          priceId: price.priceId,
          value: price.value,
        })) ?? [],
    });
  }, [modifier, reset]);

  return (
    <Form {...form}>
      <form
        id="product-modifier-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("name")}</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.position")}</FormLabel>
              <FormControl>
                <ModalInput min={1} type="number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="prices"
          render={({ field }) => (
            <FormItem>
              <Table
                className={cn(
                  form.formState.errors.prices && "border-2 border-red-500",
                )}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("Product.price_name")}</TableHead>
                    <TableHead className="text-right">
                      {t("Product.price_value")}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {field.value.map(({ priceId, value }) => {
                    const price = prices.find((p) => p.id === priceId);
                    return (
                      <ModalInput
                        key={priceId}
                        onChange={(value) => {
                          field.onChange(
                            field.value.map((price) => {
                              if (price.priceId === priceId) {
                                return {
                                  ...price,
                                  value,
                                };
                              }

                              return price;
                            }),
                          );
                        }}
                        type="decimal"
                        value={value}
                      >
                        <TableRow role="button">
                          <TableCell className="font-medium">
                            {price?.name}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatIntToCurrency(value)}
                          </TableCell>
                        </TableRow>
                      </ModalInput>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Button
                        onClick={() => {
                          handlePriceFormOpen({});
                        }}
                        size="card"
                        type="button"
                      >
                        {t("Product.add_price")}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              {/* {form.formState.errors.prices && (
                        <span className="text-center text-lg text-red-500">
                          {form.formState.errors.prices.message}
                        </span>
                      )} */}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="vatRateId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.vat_rate")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select VAT rate..."
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={vatRateOptions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="turnoverGroupId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.turnover_group")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder={t("Product.turnover_group_placeholder")}
                  maxSelect={1}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={turnoverGroupOptions}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="modifierGroupIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Product.modifier_groups")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select modifier groups..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={modifierGroupOptions}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <Dialog modal={true} onOpenChange={setPriceFormOpen} open={priceFormOpen}>
        <DialogContent>
          <DialogHeader />
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
            <ProductPriceForm
              onClose={({ priceId, value }) => {
                handlePriceFormClose();
                form.setValue(
                  "prices",
                  [
                    ...form
                      .getValues()
                      .prices.filter((price) => price.priceId !== priceId),
                    {
                      priceId,
                      value,
                    },
                  ],
                  { shouldDirty: true, shouldValidate: true },
                );
              }}
            />
          </div>
          <DialogFooter>
            <Button type="submit" form="product-price-form" variant="success">
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
